<template>
  <div class="row article-editor-hosting-row justify-content-center relative">
    <div class="mb-4 col-sm-12 col-md-10 d-lg-none">
      <base-button
        type="primary"
        size="md"
        class="mx-0 w-100"
        style="font-size: 12px !important"
        @click="showParametersModal = true"
        >PARAMÈTRES</base-button
      >
    </div>
    <div class="col-lg-2 d-flex justify-content-center">
      <BuilderSideBar
        type="article"
        :editable="editable"
        :knowledge-id="content.knowledge"
        :root-id="content.id"
        :node-id="content.id"
        :content="content"
        :user-permissions="content.userPermissions"
        :is-published="content.published"
        :generating-summary="generatingSummary"
        :generating-short-summary="generatingShortSummary"
        :generating-action-items="generatingActionItems"
        :generating-guide="generatingGuide"
        :ai-drawer-tab="focusedAiDrawerTab"
        :minimum-content-size="minimumContentSize"
        @add-notification="$emit('add-notification', $event)"
        @update-content="updateContent"
        @generate-content="handleDisplayAiDrawer"
        @generate-guide="displayArticleToGuideModal = true"
        @click:historization="sessionHistorization"
      />
    </div>

    <div
      class="col-lg-8 col-md-10 col-sm-12 new-article-editor"
      @mouseleave="onMouseLeave"
    >
      <ParametricContentThreads
        v-if="!isTemplate && !isArchive && editable"
        :content-id="content.id"
      />

      <MaydayArticleEditor
        :content-id="`${content.id}-${content.returnLanguage}`"
        :label="content.label"
        :body="content.body"
        :parents="content.parents"
        :attached-objects="content.attachedObjects"
        :editable="isContentEditable"
        @update-written-content="updateWrittenContent"
        @generate-summary="handleGenerateSummary"
        @generate-action-items="handleGenerateActionItems"
      />
      <SatisfactionActions
        v-if="!editable && !isArchive && !isTemplate"
        :content-id="content.id"
        :content="content"
        @open-feedback-modal="openFeedbackModal"
      />
    </div>
    <Transition name="collapse">
      <AiDrawer
        @close="handleCloseAiDrawer"
        @summary-generated="saveSummary"
        @action-items-generated="saveActionItems"
        @generating-summary="generatingSummary = true"
        @generating-short-summary="generatingShortSummary = true"
        @generating-action-items="generatingActionItems = true"
        @expand="
          (e) => {
            focusedAiDrawerTab = e;
          }
        "
        v-if="displayAiDrawerConditions"
        :summary="content.summary"
        :short-summary="content.shortSummary"
        :action-items="content.actionItems"
        :content-id="content.id"
        :lang="content.returnLanguage"
        :generating-summary="generatingSummary"
        :generating-short-summary="generatingShortSummary"
        :generating-action-items="generatingActionItems"
        :focused-tab-from-parent="focusedAiDrawerTab"
        :docLastUpdate="content.bodyUpdatedAt"
      />
    </Transition>
    <div class="col-lg-2 px-0 side-pannel">
      <ParameterSideBar
        :content="content"
        v-if="$route.name.includes('knowledge') && !isTemplate"
      />
      <ProductParameterSideBar
        v-else-if="$route.name.includes('web-content')"
        :enriched-parameters="content.enrichedParameters"
      />
    </div>
    <ParametersModal
      v-if="showParametersModal"
      :enriched-parameters="content.enrichedParameters"
      :display="showParametersModal"
      @close="showParametersModal = false"
    />
    <ArticleToGuideModal
      v-if="displayArticleToGuideModal"
      :body="this.content.body"
      :display="displayArticleToGuideModal"
      :label="this.content.label"
      :language="this.content.returnLanguage"
      @close="displayArticleToGuideModal = false"
      @generate-guide="handleGenerateGuide"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

// COMPONENTS
import BuilderSideBar from './ParametricBuilderSibeBar/BuilderSideBar';
import MaydayArticleEditor from 'components/Editors/MaydayArticleEditor';
import SatisfactionActions from './SatisfactionActions';
import ParameterSideBar from './ParametricParameterSideBar/ParameterSideBar';
import ParametersModal from '@/views/KnowledgeEditor/KnowledgeEditorMain/ProductViewer/ParametersModal';
import ProductParameterSideBar from '@/views/KnowledgeEditor/KnowledgeEditorMain/ProductViewer/ProductParameterSideBar';
import ParametricContentThreads from './ParametricContentThreads/ParametricContentThreads';
import AiDrawer from 'components/AI/AiDrawer.vue';
import ArticleToGuideModal from 'components/AI/ArticleToGuide/ArticleToGuideModal.vue';

export default {
  name: 'parametric-article-builder',
  components: {
    ArticleToGuideModal,
    AiDrawer,
    BuilderSideBar,
    MaydayArticleEditor,
    SatisfactionActions,
    ParameterSideBar,
    ParametersModal,
    ProductParameterSideBar,
    ParametricContentThreads,
  },
  props: {
    content: Object,
    editable: {
      type: Boolean,
      default: true,
    },
    isContentEditable: {
      type: Boolean,
      default: true,
    },
    isArchive: {
      type: Boolean,
      default: false,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
    aiDrawerTab: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      showParametersModal: false,
      generatingSummary: false,
      generatingShortSummary: false,
      generatingActionItems: false,
      displayAiDrawer: false,
      focusedAiDrawerTab: null,
      minimumContentSize: 300,
      displayArticleToGuideModal: false,
    };
  },
  computed: {
    displayAiDrawerConditions() {
      return (
        this.displayAiDrawer &&
        (this.companyAllowSummaryPreference ||
          this.companyAllowActionItemsPreference)
      );
    },
    generatingGuide() {
      return !!this.articleToGuideProcesses.find(
        (process) => process.articleId === this.content.id,
      );
    },
    ...mapState(['session']),
    ...mapGetters([
      'companyAllowSummaryPreference',
      'companyAllowActionItemsPreference',
    ]),
    ...mapGetters('aiSearchModule', ['articleToGuideProcesses']),
    ...mapGetters('knowledgeModule', ['focusKnowledge']),
  },
  methods: {
    openFeedbackModal() {
      this.$emit('open-feedback-modal');
    },
    async sessionHistorization(payload) {
      try {
        this.loadingMessage = this.$message({
          duration: 0,
          dangerouslyUseHTMLString: true,
          iconClass: 'display: none',
          showClose: true,
          message: `<i class="fas fa-spinner fa-spin" style="margin-right: 1rem;"></i> ${this.$t(
            'knowledge.article-builder.side-bar.historization.loading',
          )}`,
        });
        await this.$services.parametricContents.sessionHistorization({
          sessionId: this.session,
          solution: {
            id: this.content.id,
            ...payload,
            url: window.location.href,
          },
        });
        this.loadingMessage.close();
        this.$message({
          message: this.$t(
            'knowledge.article-builder.side-bar.historization.success',
          ),
          type: 'success',
          center: true,
        });
      } catch (error) {
        this.loadingMessage.close();
        this.$message({
          message: this.$t(
            'knowledge.article-builder.side-bar.historization.error',
          ),
          type: 'error',
          center: true,
        });
      }
    },
    updateContent(payload) {
      this.$emit('update-content', {
        id: this.content.id,
        payload,
      });
    },
    updateWrittenContent(payload) {
      this.$emit('update-written-content', {
        id: this.content.id,
        payload,
      });
    },
    onMouseLeave({ relatedTarget }) {
      if (!relatedTarget) return;

      const { className } = relatedTarget;
      const handlerClass = 'mayday-editor__drag-icon-container';

      if (className === handlerClass) return;

      const editorDragHandleElement =
        document.getElementsByClassName(handlerClass);

      if (!editorDragHandleElement || !editorDragHandleElement[0]) return;

      editorDragHandleElement[0].style.visibility = 'hidden';
    },
    handleGenerateSummary(isShortSummary) {
      this.handleDisplayAiDrawer({
        target: isShortSummary ? 'short-summary' : 'summary',
        newGeneration: isShortSummary
          ? !this.content.shortSummary.body
          : !this.content.summary.body,
      });
    },
    handleGenerateActionItems() {
      this.handleDisplayAiDrawer({
        target: 'action-items',
        newGeneration: !this.content.actionItems.body,
      });
    },
    async handleGenerateGuide(prompt) {
      this.displayArticleToGuideModal = false;
      this.updateArticleToGuideProcess({
        processes: [
          ...this.articleToGuideProcesses,
          {
            articleId: this.content.id,
            status: 'PENDING',
            hidden: false,
            id: '',
          },
        ],
      });
      const process = await this.fetchArticleToGuide({
        label: this.content.label,
        body: this.content.body,
        language: this.content.returnLanguage,
        knowledge: this.content.knowledgeId,
        knowledgeId: this.focusKnowledge.id,
        caseParameters: this.content.caseParameters,
        objective: prompt,
      });
      if (process) {
        this.updateArticleToGuideProcess({
          processes: this.articleToGuideProcesses.map((p) => {
            if (p.articleId === this.content.id) {
              return {
                ...p,
                id: process.data.id,
              };
            }
            return p;
          }),
        });
      } else {
        this.updateArticleToGuideProcess({
          processes: this.articleToGuideProcesses.map((p) => {
            if (p.articleId === this.content.id) {
              return {
                ...p,
                status: 'ERROR',
              };
            }
            return p;
          }),
        });
      }
    },
    handleDisplayAiDrawer(payload) {
      const { target, newGeneration } = payload;
      if (this.content.body.length < this.minimumContentSize) {
        this.$message({
          message: this.$t('knowledge.ai-drawer.error-empty'),
          type: 'error',
        });
        return;
      }
      this.displayAiDrawer = true;
      setTimeout(() => {
        this.focusedAiDrawerTab = target;
        if (newGeneration) {
          if (target === 'summary') this.generatingSummary = true;
          else if (target === 'short-summary')
            this.generatingShortSummary = true;
          else if (target === 'action-items') this.generatingActionItems = true;
        }
      }, 100);
    },
    handleCloseAiDrawer() {
      this.displayAiDrawer = false;
      this.focusedAiDrawerTab = null;
    },
    saveSummary(payload, isShortSummary = false) {
      if (isShortSummary) this.generatingShortSummary = false;
      else this.generatingSummary = false;

      if (payload.error) {
        this.$message({
          message: this.$t('knowledge.ai-drawer.error'),
          type: 'error',
        });
        return;
      }

      if (
        (isShortSummary && payload.body === this.content.shortSummary.body) ||
        (!isShortSummary && payload.body === this.content.summary.body)
      )
        return;

      const summariesPayload = { shortSummary: {}, summary: {} };
      if (isShortSummary) {
        summariesPayload.shortSummary = payload;
        if (payload.isVisible) summariesPayload.summary = { isVisible: false };
      } else {
        summariesPayload.summary = payload;
        if (payload.isVisible)
          summariesPayload.shortSummary = { isVisible: false };
      }

      this.$emit('update-content', {
        id: this.content.id,
        payload: summariesPayload,
      });
      this.content.summary = {
        ...this.content.summary,
        ...summariesPayload.summary,
        bodyUpdatedAt: summariesPayload.summary.body
          ? Date.now().toString()
          : this.content.summary.bodyUpdatedAt,
      };
      this.content.shortSummary = {
        ...this.content.shortSummary,
        ...summariesPayload.shortSummary,
        bodyUpdatedAt: summariesPayload.shortSummary.body
          ? Date.now().toString()
          : this.content.shortSummary.bodyUpdatedAt,
      };
    },
    saveActionItems(payload) {
      this.generatingActionItems = false;
      if (payload.error) {
        this.$message({
          message: this.$t('knowledge.ai-drawer.error'),
          type: 'error',
        });
        return;
      }
      this.$emit('update-content', {
        id: this.content.id,
        payload: {
          actionItems: payload,
        },
      });
      this.content.actionItems = {
        ...this.content.actionItems,
        ...payload,
        bodyUpdatedAt: payload.body
          ? Date.now().toString()
          : this.content.actionItems.bodyUpdatedAt,
      };
    },
    ...mapActions('aiSearchModule', [
      'fetchArticleToGuide',
      'updateArticleToGuideProcess',
    ]),
  },
  watch: {
    content: {
      handler(newValue, oldValue) {
        if ((newValue && !oldValue) || newValue.id !== oldValue.id) {
          this.focusedAiDrawerTab = null;
          this.displayAiDrawer =
            (!!newValue.summary &&
              !!newValue.summary.body &&
              !!newValue.summary.body.length) ||
            (!!newValue.shortSummary &&
              !!newValue.shortSummary.body &&
              !!newValue.shortSummary.body.length) ||
            (!!newValue.actionItems &&
              !!newValue.actionItems.body &&
              !!newValue.actionItems.body.length);
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style scoped>
@media only screen and (max-device-width: 992px) {
  .side-pannel {
    display: none;
  }
}

.side-pannel {
  z-index: 0;
}

.new-article-editor {
  margin-bottom: 30vh;
}
</style>
